import React, { useState, useEffect, useMemo } from 'react';
import {
  View,
  Text,
  TextInput,
  FlatList,
  TouchableOpacity,
  StyleSheet,
  KeyboardAvoidingView,
  Platform,
} from '../components/rn';
import ActivityIndicator from '../components/LoadingIndicator';
import { spacing, fontSize, ThemeColors } from '../theme';
import { useTheme } from '../theme/ThemeContext';
import { useTranslation } from '../i18n/I18nContext';
import { BASE_URL } from '../services/api';
import { localizedCountry } from '../data/country-names';

type University = {
  id: number;
  name: string;
  country: string;
  country_code: string | null;
};

const ISCED_LEVELS = [5, 6, 7, 8] as const;

type Props = {
  onSelect: (university: University, iscedLevel: number) => void;
  onSkip: () => void;
};

export default function UniversityScreen({ onSelect, onSkip }: Props) {
  const colors = useTheme();
  const { t, lang } = useTranslation();
  const styles = useMemo(() => makeStyles(colors), [colors]);
  const [step, setStep] = useState<'university' | 'level'>('university');
  const [pickedUniversity, setPickedUniversity] = useState<University | null>(null);
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<University[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setResults([]);
    setLoading(false);
    if (step !== 'university' || query.length < 2) return;

    let active = true;
    const timer = setTimeout(async () => {
      setLoading(true);
      try {
        const res = await fetch(`${BASE_URL}/universities?q=${encodeURIComponent(query)}&lang=${lang}`);
        if (!res.ok) return;
        const data = await res.json();
        if (active) setResults(Array.isArray(data) ? data : []);
      } catch {
        if (active) setResults([]);
      } finally {
        if (active) setLoading(false);
      }
    }, 300);

    return () => { active = false; clearTimeout(timer); };
  }, [query, lang, step]);

  if (step === 'level' && pickedUniversity) {
    return (
      <View style={styles.container}>
        <View style={styles.inner}>
          <Text style={styles.title}>{t('profile.educationLevel')}</Text>
          <Text style={styles.subtitle}>{pickedUniversity.name}</Text>

          <FlatList
            data={ISCED_LEVELS as unknown as number[]}
            keyExtractor={(level) => String(level)}
            renderItem={({ item: level }) => (
              <TouchableOpacity style={styles.row} onPress={() => onSelect(pickedUniversity, level)}>
                <Text style={styles.rowName}>{t(`isced.${level}`)}</Text>
              </TouchableOpacity>
            )}
            ItemSeparatorComponent={() => <View style={styles.separator} />}
          />

          <TouchableOpacity style={styles.skip} onPress={() => { setStep('university'); setPickedUniversity(null); }}>
            <Text style={styles.skipText}>← {t('common.back')}</Text>
          </TouchableOpacity>
        </View>
      </View>
    );
  }

  return (
    <KeyboardAvoidingView
      style={styles.container}
      behavior={Platform.OS === 'ios' ? 'padding' : 'height'}>
      <View style={styles.inner}>
        <Text style={styles.title}>{t('university.title')}</Text>
        <Text style={styles.subtitle}>{t('university.subtitle')}</Text>

        <TextInput
          style={styles.input}
          placeholder={t('university.placeholder')}
          placeholderTextColor={colors.gray400}
          value={query}
          onChangeText={setQuery}
          autoFocus
          autoCorrect={false}
        />

        {loading && <ActivityIndicator color={colors.black} style={styles.loader} />}

        <FlatList
          data={results}
          keyExtractor={(item) => String(item.id)}
          keyboardShouldPersistTaps="handled"
          renderItem={({ item }) => (
            <TouchableOpacity style={styles.row} onPress={() => { setPickedUniversity(item); setStep('level'); }}>
              <Text style={styles.rowName}>{item.name}</Text>
              <Text style={styles.rowCountry}>{localizedCountry(item.country_code, item.country, lang)}</Text>
            </TouchableOpacity>
          )}
          ItemSeparatorComponent={() => <View style={styles.separator} />}
        />

        <TouchableOpacity style={styles.skip} onPress={onSkip}>
          <Text style={styles.skipText}>{t('university.skip')}</Text>
        </TouchableOpacity>
      </View>
    </KeyboardAvoidingView>
  );
}

const makeStyles = (colors: ThemeColors) => StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: colors.white,
  },
  inner: {
    flex: 1,
    paddingHorizontal: spacing.lg,
    paddingTop: spacing.xl * 3,
  },
  title: {
    fontSize: fontSize.xl,
    fontWeight: '700',
    color: colors.black,
    marginBottom: spacing.sm,
  },
  subtitle: {
    fontSize: fontSize.md,
    color: colors.gray600,
    marginBottom: spacing.xl,
  },
  input: {
    fontSize: fontSize.lg,
    color: colors.black,
    borderBottomWidth: 1,
    borderBottomColor: colors.black,
    paddingVertical: spacing.sm,
    marginBottom: spacing.md,
  },
  loader: {
    marginVertical: spacing.md,
  },
  row: {
    paddingVertical: spacing.md,
  },
  rowName: {
    fontSize: fontSize.md,
    color: colors.black,
    fontWeight: '500',
  },
  rowCountry: {
    fontSize: fontSize.sm,
    color: colors.gray400,
    marginTop: 2,
  },
  separator: {
    height: 1,
    backgroundColor: colors.gray100,
  },
  skip: {
    alignItems: 'center',
    paddingVertical: spacing.lg,
  },
  skipText: {
    fontSize: fontSize.md,
    color: colors.gray400,
  },
});
